/**
 * Row building for the appointment printout dialog.
 *
 * Keep this in sync with the backend, which renders the actual PDF:
 * physio-api `src/modules/transaction/logic/helpers/appointmentPrintoutRows.ts`.
 * The dialog is a preview of that PDF, so both must list the same start times.
 */

/** One row of the dialog table. `start` is an ISO timestamp, as the API delivers it. */
export interface PrintoutRow {
  start: string;
}

interface SlotLike {
  start?: string | null;
  end?: string | null;
}

interface Block {
  start: Date;
  end: Date;
}

const toBlock = (slot: SlotLike): Block | null => {
  if (!slot?.start || !slot?.end) return null;
  const start = new Date(slot.start);
  const end = new Date(slot.end);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
  return { start, end };
};

/**
 * Rows for the Rx printout: the start times of the Rx's own time slots.
 *
 * An appointment start is the earliest of ALL its slots, so it can belong to a different Rx
 * or to another point of the day. The slots handed in here already come restricted to the
 * printed Rx (GET /appointment/printEachRx/:rxId).
 *
 * Back to back slots (end of one == start of the next) are collapsed into a single start
 * time, slots with a gap between them are listed individually. Unscheduled slots are
 * dropped. Merging is purely by time, a change of therapist or therapy does not split a
 * block.
 */
export const buildRxPrintoutRows = (appointments: { timeSlots?: SlotLike[] }[]): PrintoutRow[] => {
  const blocks = (appointments ?? [])
    .flatMap((appointment) => appointment?.timeSlots ?? [])
    .map(toBlock)
    .filter((block): block is Block => block !== null)
    .sort((a, b) => a.start.getTime() - b.start.getTime());

  const rows: PrintoutRow[] = [];
  let current: Block | null = null;

  for (const block of blocks) {
    if (!current) {
      current = { ...block };
    } else if (current.end.getTime() >= block.start.getTime()) {
      if (block.end.getTime() > current.end.getTime()) current.end = block.end;
    } else {
      rows.push({ start: current.start.toISOString() });
      current = { ...block };
    }
  }
  if (current) rows.push({ start: current.start.toISOString() });

  return rows;
};

/** Rows for the patient wide printout, which still lists one row per appointment start. */
export const buildAppointmentStartRows = (
  appointments: { start?: string | null }[],
): PrintoutRow[] =>
  (appointments ?? [])
    .filter((appointment) => appointment?.start)
    .map((appointment) => ({ start: appointment.start! }));
